import { ComponentType } from 'react';

import { Blush, MagnifyingGlass, SadBrows, Sparkles, Tears, ThinkingDots, Zzz } from './Accessory.tsx';
import { Body } from './Body';
import { EyeHappy, EyeNormal, EyeProps, EyeSadLeft, EyeSadRight, EyeSleepy } from './Eye';
import { HandProps, LeftHand, RightHand } from './Hand';
import { MouthOpen, MouthSad, MouthSmile } from './Mouth';

export interface Pose {
    body: ComponentType;
    leftHand: ComponentType<HandProps>;
    leftHandProps: HandProps;
    rightHand: ComponentType<HandProps>;
    rightHandProps: HandProps;
    leftEye: ComponentType<EyeProps>;
    rightEye: ComponentType<EyeProps>;
    eyeProps: { w: number; h: number; gap: number; offX?: number; offY?: number };
    mouth?: ComponentType;
    accessories?: ComponentType[];
}

export const IDLE: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 30, y: -2, w: 62, h: 52, rot: -5 },
    rightHand: RightHand,
    rightHandProps: { x: 35, y: -2, w: 58, h: 48, rot: 5 },
    leftEye: EyeNormal,
    rightEye: EyeNormal,
    eyeProps: { w: 30, h: 48, gap: 22 },
};

export const SIT: Pose = {
    ...IDLE,
    leftHandProps: { x: 22, y: 32, w: 64, h: 26, rot: -6 },
    rightHandProps: { x: 26, y: 33, w: 60, h: 24, rot: 6 },
};

export const PEEK_SMILE: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 30, y: -2, w: 62, h: 52, rot: -20 },
    rightHand: RightHand,
    rightHandProps: { x: 35, y: -2, w: 58, h: 48, rot: 20 },
    leftEye: EyeHappy,
    rightEye: EyeHappy,
    eyeProps: { w: 32, h: 36, gap: 22 },
};

export const HAPPY: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 15, y: 10, w: 58, h: 48, rot: -20 },
    rightHand: RightHand,
    rightHandProps: { x: 15, y: 10, w: 58, h: 48, rot: 20 },
    leftEye: EyeHappy,
    rightEye: EyeHappy,
    eyeProps: { w: 32, h: 36, gap: 22 },
    mouth: MouthSmile,
    accessories: [Blush],
};

export const CURIOUS: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: -15, y: 55, w: 55, h: 45, rot: -30 },
    rightHand: RightHand,
    rightHandProps: { x: 35, y: -2, w: 58, h: 48, rot: 5 },
    leftEye: EyeNormal,
    rightEye: EyeNormal,
    eyeProps: { w: 30, h: 48, gap: 18, offX: -25, offY: 5 },
    accessories: [MagnifyingGlass],
};

export const SURPRISED: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: -10, y: 40, w: 55, h: 48, rot: -35 },
    rightHand: RightHand,
    rightHandProps: { x: -10, y: 40, w: 55, h: 48, rot: 35 },
    leftEye: EyeNormal,
    rightEye: EyeNormal,
    eyeProps: { w: 34, h: 52, gap: 26 },
    mouth: MouthOpen,
};

export const SLEEPY: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 35, y: -10, w: 65, h: 55, rot: -2 },
    rightHand: RightHand,
    rightHandProps: { x: 40, y: -10, w: 60, h: 50, rot: 2 },
    leftEye: EyeSleepy,
    rightEye: EyeSleepy,
    eyeProps: { w: 30, h: 12, gap: 22 },
    accessories: [Zzz],
};

export const WAVE: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 30, y: -2, w: 62, h: 52, rot: -5 },
    rightHand: RightHand,
    rightHandProps: { x: -20, y: 70, w: 55, h: 45, rot: 40, animate: 'waveHand' },
    leftEye: EyeNormal,
    rightEye: EyeNormal,
    eyeProps: { w: 30, h: 48, gap: 22 },
};

export const THINKING: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 30, y: -2, w: 62, h: 52, rot: -5 },
    rightHand: RightHand,
    rightHandProps: { x: -5, y: 55, w: 50, h: 42, rot: 25 },
    leftEye: EyeNormal,
    rightEye: EyeNormal,
    eyeProps: { w: 26, h: 44, gap: 20, offX: 5, offY: -3 },
    accessories: [ThinkingDots],
};

export const SAD: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: 45, y: 20, w: 64, h: 54, rot: -5 },
    rightHand: RightHand,
    rightHandProps: { x: 50, y: 20, w: 64, h: 54, rot: 5 },
    leftEye: EyeSadLeft,
    rightEye: EyeSadRight,
    eyeProps: { w: 30, h: 36, gap: 20, offY: 40 },
    mouth: MouthSad,
    accessories: [SadBrows, Tears],
};

export const SUCCESS: Pose = {
    body: Body,
    leftHand: LeftHand,
    leftHandProps: { x: -15, y: 100, w: 55, h: 48, rot: -15 },
    rightHand: RightHand,
    rightHandProps: { x: 20, y: 5, w: 55, h: 45, rot: 10 },
    leftEye: EyeHappy,
    rightEye: EyeHappy,
    eyeProps: { w: 32, h: 36, gap: 22 },
    accessories: [Blush, Sparkles],
};
